import { useCallback, useState } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import {
  runWanApply,
  wanApplyDoneText,
  wanApplyLabel,
  wanApplyPlan,
  wanApplyRefusal,
  type WanApplyChoice,
  type WanApplyResult,
} from '../../lib/wan-apply';

/**
 * The subscriber-WAN apply as BACKGROUND work — the one hand-off the WAN
 * mode wizard and the WAN card's IPv6 rows share (the bodies and the calls
 * are `lib/wan-apply.ts`; this is where they meet the API and the tray).
 *
 * The choice is captured into the task's closures because the surface that
 * started it is gone by the time the task settles: the wizard CLOSES on
 * Apply (modals close on submit; the write runs in the tray, like Resync),
 * and the card may have been collapsed or navigated away from. The toast
 * has to be able to name what was changed without either still mounted.
 *
 * `running` is what the card's Save button shows while its sequence is in
 * flight — progress the way its IPv4 Save shows it — and is cleared however
 * the task ends; the tray keeps the outcome either way.
 */
export function useWanApply(ctx: {
  /** The ONU page's `configBase` — where the VLAN rows live. */
  base: string;
  oltId: string;
  onuId: string;
  serial: string;
  /** Where the background toast/tray row links — the ONU's own page. */
  href?: string;
  /** Extra query keys to refresh once the write lands (the page's own). */
  invalidate?: QueryKey[];
}) {
  const tasks = useTasks();
  const queryClient = useQueryClient();
  const [running, setRunning] = useState(false);
  const { base, oltId, onuId, serial, href, invalidate } = ctx;

  const start = useCallback(
    (
      choice: WanApplyChoice,
      opts: {
        /**
         * Told how the sequence ended, for a surface that stays mounted:
         * the card drops its draft only on `ok`, so a write the chassis
         * refused leaves the chosen radios in place beside the toast's
         * sentence rather than snapping back as if nothing was asked.
         */
        onSettled?: (ok: boolean) => void;
      } = {},
    ) => {
      const plan = wanApplyPlan(choice);
      const onu = `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}`;
      // The rows the write moved: the VLAN rows and the `wan-ipv6` read
      // under `['onu-config', base]`, the platform's firewall column under
      // the omci-config read, and whatever the caller's page shows.
      const moved: QueryKey[] = [['onu-config', base], ['onu', onuId, 'omci-config'], ...(invalidate ?? [])];
      setRunning(true);
      tasks.start<WanApplyResult>({
        label: wanApplyLabel(choice, serial),
        ...(href ? { href } : {}),
        run: () =>
          runWanApply(plan, { rowsBase: `${base}/vlans`, wan: `${onu}/omci-wan`, firewall: `${onu}/firewall` }, api).then(
            async (result) => {
              /*
                Re-read HERE, before the surface is told, not through the
                tray's own `invalidate` (which runs after `run` resolves):
                the card's rows open on the record, so its draft must be
                dropped only once the record it just wrote is what the
                cache holds. Dropping it first showed the OLD record for a
                round-trip and then the new one (review, 2026-09-14).
                `invalidateQueries` resolves when the refetch is done.
              */
              await Promise.all(moved.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
              setRunning(false);
              /*
                The OLT refused the VLAN: a failure, said in the toast with the
                OLT's own answer, which stays until it is read (2026-10-14 —
                it used to settle green and send the operator to a page that
                never showed the answer). The card keeps its draft.
              */
              if (wanApplyRefusal(result.saved.applied)) {
                opts.onSettled?.(false);
                throw new Error(wanApplyDoneText(choice, result, serial));
              }
              opts.onSettled?.(true);
              return result;
            },
            (err: unknown) => {
              setRunning(false);
              opts.onSettled?.(false);
              throw err;
            },
          ),
        done: (result) => wanApplyDoneText(choice, result, serial),
      });
    },
    [tasks, queryClient, base, oltId, onuId, serial, href, invalidate],
  );

  return { start, running };
}
